import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { ScheduleWeek } from '@Models';
import { formatWeekOf } from './constants';

const STEP_BUTTON_CLASS_NAME =
  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 ' +
  'hover:bg-gray-50 hover:text-gray-700 disabled:pointer-events-none disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-300';

interface PeriodNavigatorProps {
  weeks: ScheduleWeek[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

const PeriodNavigator = ({ weeks, selectedIndex, onSelect }: PeriodNavigatorProps) => {
  const { t, i18n } = useTranslation();
  const week = weeks[selectedIndex];
  const weekOf = t('Schedule.Detail.Sidebar.Period.WeekOf', { date: formatWeekOf(week.week_start, i18n.language) });
  const position = { current: selectedIndex + 1, total: weeks.length };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label={t('Schedule.Detail.Sidebar.Period.PreviousAriaLabel')}
          disabled={selectedIndex === 0}
          onClick={() => onSelect(selectedIndex - 1)}
          className={STEP_BUTTON_CLASS_NAME}>
          <ChevronLeft size={14} />
        </button>
        <div className="text-center text-sm font-semibold text-gray-900">
          {week.label ? `${week.label} · ${weekOf}` : weekOf}
        </div>
        <button
          type="button"
          aria-label={t('Schedule.Detail.Sidebar.Period.NextAriaLabel')}
          disabled={selectedIndex === weeks.length - 1}
          onClick={() => onSelect(selectedIndex + 1)}
          className={STEP_BUTTON_CLASS_NAME}>
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="flex gap-1">
        {weeks.map((entry, index) => (
          <div
            key={entry.week_start}
            className={cn(
              'h-[3px] flex-1 rounded-full',
              index === selectedIndex ? 'bg-brand-500' : entry.pending_count === 0 ? 'bg-success-400' : 'bg-gray-150',
            )}
          />
        ))}
      </div>
      <div className="text-center text-xs text-gray-500">
        {week.has_started
          ? t('Schedule.Detail.Sidebar.Period.PositionStarted', position)
          : week.pending_count > 0
          ? t('Schedule.Detail.Sidebar.Period.PositionPending', { ...position, count: week.pending_count })
          : t('Schedule.Detail.Sidebar.Period.PositionApproved', position)}
      </div>
    </div>
  );
};

export default PeriodNavigator;
